import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const SEARCH_DELAY_MS = 300;

export function useSectionParams(section: string) {
  const [params, setParams] = useSearchParams();

  function read(key: string, fallback = ''): string {
    return params.get(`${section}.${key}`) ?? fallback;
  }

  function write(next: Record<string, string | null>, resetPage = true) {
    setParams((current) => {
      const copy = new URLSearchParams(current);
      for (const [key, value] of Object.entries(next)) {
        const name = `${section}.${key}`;
        if (value === null || value === '' || value === 'all' || value === 'All') copy.delete(name);
        else copy.set(name, value);
      }
      if (resetPage) copy.delete(`${section}.page`);
      return copy;
    }, { replace: true });
  }

  const page = Math.max(1, Number(read('page', '1')) || 1);
  const sort = read('sort');
  const direction = read('dir') === 'asc' || read('dir') === 'desc' ? read('dir') as 'asc' | 'desc' : '';
  const search = read('q');

  return { read, write, page, sort, direction, search };
}

export function useDebouncedSearch(section: string, urlValue: string, write: (next: Record<string, string | null>) => void) {
  const [draft, setDraft] = useState(urlValue);
  const [seen, setSeen] = useState(urlValue);
  const timer = useRef<number | null>(null);

  if (urlValue !== seen) {
    setSeen(urlValue);
    setDraft(urlValue);
  }

  useEffect(() => () => {
    if (timer.current !== null) window.clearTimeout(timer.current);
  }, []);

  function onSearch(value: string) {
    setDraft(value);
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      write({ q: value.trim() === '' ? null : value.trim() });
    }, SEARCH_DELAY_MS);
  }

  return { draft, onSearch, section };
}
